import { Button as MantineButton, type ButtonProps as MantineButtonProps } from "@mantine/core";
import type { ReactElement, ReactNode, RefObject } from "react";
import clsx from "clsx";
import styles from "./Button.module.scss";
import { MenuButton, type MenuButtonProps } from "./MenuButton/MenuButton";
import { TextButton, type TextButtonProps } from "./TextButton/TextButton";
import { IconButton, type IconButtonProps } from "./IconButton/IconButton";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "neutral-primary"
  | "neutral-secondary"
  | "destructive-primary"
  | "destructive-secondary";

type BaseButtonProps = {
  children?: ReactNode;
  isDisabled?: boolean;
  iconLeft?: ReactElement;
  iconRight?: ReactElement;
  variant?: ButtonVariant;
  size?: "small" | "base" | "large";
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  ref?: RefObject<HTMLButtonElement>;
} & Omit<MantineButtonProps, "variant" | "size">;

export type BrandButtonProps =
  | (BaseButtonProps & { type?: "button" | "submit" | "reset" })
  | (IconButtonProps & { type: "icon" })
  | (TextButtonProps & { type: "text" })
  | (MenuButtonProps & { type: "dropdown" });

export function Button(props: BrandButtonProps) {
  // Dropdown type
  if (props.type === "dropdown") {
    return <MenuButton {...props} />;
  }

  // Text type
  if (props.type === "text") {
    return <TextButton {...props} />;
  }

  // Icon type
  if (props.type === "icon") {
    return <IconButton {...props} />;
  }

  // Regular Button
  const { variant, size = "base", isDisabled, children, iconLeft, iconRight, ...rest } = props;

  const variantClassName = styles[`button-${variant}`] || "";
  const sizeClassName = styles[`button-size-${size}`];
  const finalClassName = isDisabled
    ? clsx(styles["button-disabled"], styles["base-button"], sizeClassName)
    : clsx(variantClassName, styles["base-button"], sizeClassName);

  const isOutlineVariant =
    variant === "secondary" ||
    variant === "tertiary" ||
    variant === "neutral-secondary" ||
    variant === "destructive-secondary";

  return (
    <MantineButton
      className={finalClassName}
      variant={isOutlineVariant ? "outline" : "filled"}
      disabled={isDisabled}
      type={props?.type}
      ref={props.ref as RefObject<HTMLButtonElement>}
      {...rest}
    >
      {iconLeft && <span className={styles["icon-left"]}>{iconLeft}</span>}
      {children}
      {iconRight && <span className={styles["icon-right"]}>{iconRight}</span>}
    </MantineButton>
  );
}

// Re-export sub-components
export { MenuButton, TextButton, IconButton };
export type { MenuButtonProps, TextButtonProps, IconButtonProps };
